import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slide,
  IconButton,
  Stack,
  alpha,
  useTheme
} from '@mui/material';
import { Close as CloseIcon, CheckCircle as CheckCircleIcon, AccessTime as AccessTimeIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../util/auth';
import { completeBankBonus } from '../util/firebase';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function CompleteBonusModal({ 
  open, 
  onClose, 
  bank, 
  existingBonus, 
  onComplete
}) {
  const theme = useTheme();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [isCompleting, setIsCompleting] = useState(false);
  const isDark = theme.palette.mode === 'dark';

  const handleComplete = async (isPaidOut) => {
    try {
      setIsCompleting(true);
      const updatedBonus = await completeBankBonus(user.uid, bank.id, isPaidOut, bank.amount);
      
      // Update parent component state
      if (onComplete) {
        onComplete(updatedBonus);
      }
      
      enqueueSnackbar('Bonus marked as completed', { variant: 'success' });
      onClose();
    } catch (error) {
      console.error('Error completing bonus:', error);
      enqueueSnackbar('Failed to complete bonus', { variant: 'error' });
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isDark ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
          backgroundImage: 'none',
          boxShadow: `0 8px 32px ${isDark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.1)'}`,
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        Complete Bonus
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
            {bank?.name} - ${bank?.amount} Bonus
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Have you received the bonus payout?
          </Typography>
          <Stack spacing={2}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              size="large"
              onClick={() => handleComplete(true)}
              startIcon={<CheckCircleIcon />}
              sx={{
                py: 2,
                bgcolor: theme.palette.success.main,
                '&:hover': {
                  bgcolor: theme.palette.success.dark,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.4)}`,
                },
              }}
              disabled={isCompleting}
            >
              {isCompleting ? 'Processing...' : 'Yes, Bonus Paid Out'}
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="warning"
              size="large"
              onClick={() => handleComplete(false)}
              startIcon={<AccessTimeIcon />}
              sx={{
                py: 2,
                bgcolor: theme.palette.warning.main,
                '&:hover': {
                  bgcolor: theme.palette.warning.dark,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.4)}`,
                },
              }}
              disabled={isCompleting}
            >
              {isCompleting ? 'Processing...' : 'No, Still Waiting for Payout'}
            </Button>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose}
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              bgcolor: isDark ? alpha(theme.palette.grey[100], 0.08) : alpha(theme.palette.grey[900], 0.08),
            },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
} 